/**
 * PersonaChipList — persona suggestion chips for canvas sections
 *
 * Renders market:Persona entities as suggestion chips below the existing
 * section items. Each chip shows a mini-popover on click with name, role,
 * top 2 goals, and top 2 pains from the linked workshop session (if any).
 */

import { useState, useRef, useEffect } from 'react'
import { User, X } from 'lucide-react'
import type { EntityTreeInstance } from '@/lib/api/client'
import type { PersonaPopoverData } from '@/hooks/usePersonasForCanvas'
import { badgeVariants } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import type { CanvasSectionTheme } from './canvasTheme'

// ============================================================================
// TYPES
// ============================================================================

interface PersonaChipListProps {
  personas: EntityTreeInstance[]
  sessionMap: Map<string, PersonaPopoverData>
  sectionTheme: CanvasSectionTheme
  sectionColor: string
}

interface PopoverState {
  uri: string
  rect: DOMRect
}

// ============================================================================
// MINI-POPOVER
// ============================================================================

function PersonaPopover({
  persona,
  sessionData,
  anchorRect,
  onClose,
}: {
  persona: EntityTreeInstance
  sessionData: PersonaPopoverData | undefined
  anchorRect: DOMRect
  onClose: () => void
}) {
  const popoverRef = useRef<HTMLDivElement>(null)
  const initials = persona.label
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase() || '?'

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose()
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  // Position: below the chip, clamped to viewport
  const top = anchorRect.bottom + 6
  const left = Math.min(anchorRect.left, window.innerWidth - 220)

  return (
    <div
      ref={popoverRef}
      style={{
        position: 'fixed',
        top,
        left,
        width: 212,
        backgroundColor: 'white',
        border: '1px solid var(--color-border)',
        borderRadius: '10px',
        boxShadow: '0 4px 20px rgba(29,26,21,0.12)',
        zIndex: 9999,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 10px 8px',
        backgroundColor: 'var(--color-market)',
        background: 'linear-gradient(135deg, var(--color-market-600) 0%, var(--color-market) 100%)',
      }}>
        <div style={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          backgroundColor: 'rgba(255,255,255,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          fontSize: 12,
          fontWeight: 600,
          flexShrink: 0,
        }}>
          {initials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: 'white', fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>
            {persona.label}
          </div>
          {sessionData?.role && (
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 10, marginTop: 1 }}>
              {sessionData.role}
            </div>
          )}
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            color: 'rgba(255,255,255,0.7)',
            cursor: 'pointer',
            padding: 2,
            display: 'flex',
            flexShrink: 0,
          }}
        >
          <X size={13} />
        </button>
      </div>

      {/* Body */}
      <div style={{ padding: '8px 10px' }}>
        {sessionData?.goals && sessionData.goals.length > 0 && (
          <div style={{ marginBottom: 6 }}>
            <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              Goals
            </div>
            {sessionData.goals.map((g, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--color-text)', lineHeight: 1.4, display: 'flex', gap: 4, alignItems: 'flex-start', marginBottom: 2 }}>
                <span style={{ color: 'var(--color-market)', marginTop: 2 }}>→</span>
                <span>{g}</span>
              </div>
            ))}
          </div>
        )}
        {sessionData?.pains && sessionData.pains.length > 0 && (
          <div>
            <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 }}>
              Pains
            </div>
            {sessionData.pains.map((p, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--color-text)', lineHeight: 1.4, display: 'flex', gap: 4, alignItems: 'flex-start', marginBottom: 2 }}>
                <span style={{ color: 'var(--color-danger)', marginTop: 2 }}>!</span>
                <span>{p}</span>
              </div>
            ))}
          </div>
        )}
        {(!sessionData || (!sessionData.goals.length && !sessionData.pains.length)) && (
          <p style={{ fontSize: 11, color: 'var(--color-text-muted)', margin: 0 }}>
            No workshop data yet.
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// CHIP LIST
// ============================================================================

export function PersonaChipList({ personas, sessionMap, sectionTheme, sectionColor }: PersonaChipListProps) {
  const [popover, setPopover] = useState<PopoverState | null>(null)

  if (personas.length === 0) return null

  const activePersona = popover ? personas.find(p => p.uri === popover.uri) : undefined

  return (
    <>
      {/* Separator */}
      <div style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        margin: '4px 0 2px',
      }}>
        <div style={{ flex: 1, height: 1, backgroundColor: `${sectionColor}20` }} />
        <span style={{ fontSize: 9, color: 'var(--color-text-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' }}>
          Personas in graph
        </span>
        <div style={{ flex: 1, height: 1, backgroundColor: `${sectionColor}20` }} />
      </div>

      {/* Chips */}
      {personas.map(persona => (
        <button
          key={persona.uri}
          onClick={(e) => {
            e.stopPropagation()
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
            setPopover(prev => prev?.uri === persona.uri ? null : { uri: persona.uri, rect })
          }}
          className={cn(
            badgeVariants({ variant: sectionTheme }),
            'max-w-full gap-1.5 border cursor-pointer hover:shadow-sm'
          )}
          style={{
            borderColor: 'transparent',
            transition: 'all 0.12s',
          }}
          title="Click to see persona details"
        >
          <User size={10} style={{ color: sectionColor, flexShrink: 0 }} />
          <span className="block max-w-[100px] truncate" style={{ color: sectionColor }}>
            {persona.label}
          </span>
        </button>
      ))}

      {/* Popover (portal-like via fixed positioning) */}
      {popover && activePersona && (
        <PersonaPopover
          persona={activePersona}
          sessionData={sessionMap.get(activePersona.uri)}
          anchorRect={popover.rect}
          onClose={() => setPopover(null)}
        />
      )}
    </>
  )
}
